import React, { useState, useEffect } from "react";
import { Filter, ChevronDown, ChevronUp } from "lucide-react";
import { useAppState } from "../../../hooks/useAppState";

const FilterModelTeam = ({
  sections,
  setSections,
  customWallet,
  setCustomWallet,
  customAmount,
  setCustomAmount,
  setFilterViewModel,
  onApplyFilters,
  activeTab,
  appliedFilters = {}, // Add appliedFilters prop to receive current applied filters
}) => {
  const [localFilters, setLocalFilters] = useState({
    status: "",
    role: "",
    walletRange: [] as string[],
    amountRange: [] as string[],
    customWalletMin: "",
    customWalletMax: "",
    customAmountMin: "",
    customAmountMax: "",
    rating: "",
  });

  // Store the initially applied filters when component mounts
  const [initialAppliedFilters, setInitialAppliedFilters] = useState({
    status: "",
    role: "",
    walletRange: [] as string[],
    amountRange: [] as string[],
    customWalletMin: "",
    customWalletMax: "",
    customAmountMin: "",
    customAmountMax: "",
    rating: "",
  });

  const { user } = useAppState();

  // Validation error states
  const [walletCustomMaxError, setWalletCustomMaxError] = useState("");
  const [amountCustomMaxError, setAmountCustomMaxError] = useState("");

  // Validation helper function
  const validateMaxValue = (min: string, max: string): string => {
    if (!max) return "";
    if (!min) return "";
    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);
    if (maxNum <= minNum) {
      return "Max must be greater than Min";
    }
    return "";
  };

  // Check if there are any validation errors
  const hasValidationErrors = (): boolean => {
    return !!(walletCustomMaxError || amountCustomMaxError);
  };

  // Initialize local filters with applied filters when component mounts
  useEffect(() => {
    const currentFilters = {
      status: (appliedFilters as any)?.status || "",
      role: (appliedFilters as any)?.role || "",
      walletRange: (appliedFilters as any)?.walletRange || [],
      amountRange: (appliedFilters as any)?.amountRange || [],
      customWalletMin: (appliedFilters as any)?.customWalletMin || "",
      customWalletMax: (appliedFilters as any)?.customWalletMax || "",
      customAmountMin: (appliedFilters as any)?.customAmountMin || "",
      customAmountMax: (appliedFilters as any)?.customAmountMax || "",
      rating: (appliedFilters as any)?.rating || "",
    };

    setLocalFilters(currentFilters);
    setInitialAppliedFilters(currentFilters);

    // Set custom wallet and amount states based on applied filters
    if (
      (appliedFilters as any)?.customWalletMin ||
      (appliedFilters as any)?.customWalletMax
    ) {
      setCustomWallet(true);
    }
    if (
      (appliedFilters as any)?.customAmountMin ||
      (appliedFilters as any)?.customAmountMax
    ) {
      setCustomAmount(true);
    }
  }, [appliedFilters, setCustomWallet, setCustomAmount]);

  const handleStatusChange = (status: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: status,
    }));
  };

  const handleRoleChange = (role: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      role: role,
    }));
  };

  const handleWalletRangeChange = (range: string) => {
    // If custom wallet is enabled, disable it when selecting a range
    if (customWallet) {
      setCustomWallet(false);
    }
    setLocalFilters((prev) => ({
      ...prev,
      walletRange: prev.walletRange.includes(range)
        ? prev.walletRange.filter((r) => r !== range)
        : [...prev.walletRange, range],
    }));
  };

  const handleAmountRangeChange = (range: string) => {
    // If custom amount is enabled, disable it when selecting a range
    if (customAmount) {
      setCustomAmount(false);
      // Clear custom amount values when switching to predefined ranges
      setLocalFilters((prev) => ({
        ...prev,
        customAmountMin: "",
        customAmountMax: "",
        amountRange: prev.amountRange.includes(range)
          ? prev.amountRange.filter((r) => r !== range)
          : [...prev.amountRange, range],
      }));
    } else {
      setLocalFilters((prev) => ({
        ...prev,
        amountRange: prev.amountRange.includes(range)
          ? prev.amountRange.filter((r) => r !== range)
          : [...prev.amountRange, range],
      }));
    }
  };

  const handleCustomWalletChange = (field: string, value: string) => {
    setLocalFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // Validate when either min or max changes
      if (field === "customWalletMin" && updated.customWalletMax) {
        setWalletCustomMaxError(validateMaxValue(value, updated.customWalletMax));
      } else if (field === "customWalletMax") {
        setWalletCustomMaxError(validateMaxValue(updated.customWalletMin, value));
      }
      
      return updated;
    });
  };

  const handleCustomAmountChange = (field: string, value: string) => {
    setLocalFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // Validate when either min or max changes
      if (field === "customAmountMin" && updated.customAmountMax) {
        setAmountCustomMaxError(validateMaxValue(value, updated.customAmountMax));
      } else if (field === "customAmountMax") {
        setAmountCustomMaxError(validateMaxValue(updated.customAmountMin, value));
      }
      
      return updated;
    });
  };

  const handleRatingChange = (rating: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      rating: rating,
    }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    // Update the initially applied filters to the new applied filters
    setInitialAppliedFilters(localFilters);
    setFilterViewModel(false);
  };

  const handleCancel = () => {
    // Revert to initially applied filters
    setLocalFilters(initialAppliedFilters);

    // Clear validation errors
    setWalletCustomMaxError("");
    setAmountCustomMaxError("");

    // Reset custom wallet and amount states to match initially applied filters
    if (
      initialAppliedFilters.customWalletMin ||
      initialAppliedFilters.customWalletMax
    ) {
      setCustomWallet(true);
    } else {
      setCustomWallet(false);
    }

    if (
      initialAppliedFilters.customAmountMin ||
      initialAppliedFilters.customAmountMax
    ) {
      setCustomAmount(true);
    } else {
      setCustomAmount(false);
    }

    // Close the filter modal
    setFilterViewModel(false);
  };

  const handleReset = () => {
    setLocalFilters({
      status: "",
      role: "",
      walletRange: [] as string[],
      amountRange: [] as string[],
      customWalletMin: "",
      customWalletMax: "",
      customAmountMin: "",
      customAmountMax: "",
      rating: "",
    });
    setCustomWallet(false);
    setCustomAmount(false);
    setWalletCustomMaxError("");
    setAmountCustomMaxError("");
    onApplyFilters({
      status: "",
      role: "",
      walletRange: [] as string[],
      amountRange: [] as string[],
      customWalletMin: "",
      customWalletMax: "",
      customAmountMin: "",
      customAmountMax: "",
      rating: "",
    });
    setInitialAppliedFilters({
      status: "",
      role: "",
      walletRange: [] as string[],
      amountRange: [] as string[],
      customWalletMin: "",
      customWalletMax: "",
      customAmountMin: "",
      customAmountMax: "",
      rating: "",
    });
  };
  return (
    <div>
      {" "}
      <div className="absolute right-0 top-12 z-20">
        <div className="w-[392px] p-4 rounded-[16px]   max-h-[67vh] overflow-y-auto  border border-[#BCBCBC] bg-white shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <Filter className="mr-2 w-[24px] h-[24px]" />
              <h2 className="text-[20px] nunito-semibold text-[#0C0C0C]">
                Filters
              </h2>
            </div>{" "}
            <button
              className="text-[#0C0C0C] text-[16px] nunito-semibold"
              onClick={handleReset}
            >
              Reset all
            </button>
          </div>
         <div className="overflow-y-auto"> 


          {/* Status Section */}
          <div className="mb-4">
            <div
              className="flex justify-between items-center mb-2 cursor-pointer"
              onClick={() =>
                setSections({ ...sections, status: !sections.status })
              }
            >
              {" "}
              <span className="nunito-semibold text-[#404040] text-[16px]">
                Status
              </span>
              {sections.status ? (
                <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
              ) : (
                <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
              )}
            </div>{" "}
            {sections.status && (
              <div className="mb-3">
                {" "}
                <select
                  className="w-full h-[40px] p-2 border border-[#A8A8A8] rounded-[8px] text-[#696969] text-[14px] nunito-semibold"
                  value={localFilters.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="">All Status</option>
                  {activeTab === "Activity" ? (
                    // Team Activity tab status options
                    <>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="issue-reported">Issue Reported</option>
                    </>
                  ) : (
                    // Directory tab status options
                    <>
                      <option value="active">Active</option>
                      <option value="deactivated">Deactivated</option>
                      <option value="invited">Invite Sent</option>
                    </>
                  )}
                </select>
              </div>
            )}
          </div>
          <div className="border-b mb-3 border-[#DFDFDF]"></div>
          {/* Role Section */}
          {activeTab !== "Activity" && (
            <>
              <div className="mb-4">
                <div
                  className="flex justify-between items-center mb-2 cursor-pointer"
                  onClick={() =>
                    setSections({ ...sections, role: !sections.role })
                  }
                >
                  {" "}
                  <span className="nunito-semibold text-[#404040] text-[16px]">
                    Role
                  </span>
                  {sections.role ? (
                    <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                  ) : (
                    <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                  )}
                </div>{" "}
                {sections.role && (
                  <div className="mb-3">
                    <select
                      className="w-full h-[40px] p-2 border border-[#A8A8A8] rounded-[8px] text-[#696969] text-[14px] nunito-semibold"
                      value={localFilters.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                    >
                      {user?.role === "Customer" && (
                        <>
                          <option value="">All Roles</option>
                          <option value="customer_team_lead">Team Lead</option>
                          <option value="customer_team_member">
                            Team Member
                          </option>
                        </>
                      )}
                      {user?.role === "Expert" && (
                        <>
                          <option value="">All Roles</option>
                          <option value="expert_team_lead">Team Lead</option>
                          <option value="expert_team_member">
                            Team Member
                          </option>
                        </>
                      )}
                    </select>
                  </div>
                )}
              </div>
              <div className="border-b mb-3 border-[#DFDFDF]"></div>{" "}
            </>
          )}{" "}
          {/* Rating Section - Only show for Team Activity tab */}
          {activeTab === "Activity" && (
            <>
              <div className="mb-4">
                <div
                  className="flex justify-between items-center mb-2 cursor-pointer"
                  onClick={() =>
                    setSections({ ...sections, rating: !sections.rating })
                  }
                >
                  {" "}
                  <span className="nunito-semibold text-[#404040] text-[16px]">
                    Rating
                  </span>
                  {sections.rating ? (
                    <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                  ) : (
                    <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                  )}
                </div>
                {sections.rating && (
                  <div className="mb-3">
                    <select
                      className="w-full h-[40px] p-2 border border-[#A8A8A8] rounded-[8px] text-[#696969] text-[14px] nunito-semibold appearance-none"
                      value={localFilters.rating}
                      onChange={(e) => handleRatingChange(e.target.value)}
                    >
                      <option value="">All Ratings</option>
                      <option value="1">1 Star</option>
                      <option value="2">2 Stars</option>
                      <option value="3">3 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="5">5 Stars</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="border-b mb-3 border-[#DFDFDF]"></div>
            </>
          )}
          {/* Wallet Section for Customer Directory */}
          {user?.role === "Customer" && activeTab !== "Activity" && (
            <div className="mb-4 mt-3">
              <div
                className="flex justify-between items-center mb-4 cursor-pointer"
                onClick={() =>
                  setSections({ ...sections, wallet: !sections.wallet })
                }
              >
                <span className="nunito-semibold text-[#404040] text-[16px]">
                  Wallet
                </span>
                {sections.wallet ? (
                  <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                ) : (
                  <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                )}
              </div>
              {sections.wallet && (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-0-100"
                        checked={localFilters.walletRange.includes("0-100")}
                        onChange={() => handleWalletRangeChange("0-100")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-0-100"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 0-100
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-101-200"
                        checked={localFilters.walletRange.includes("101-200")}
                        onChange={() => handleWalletRangeChange("101-200")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-101-200"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 101-200
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-201-300"
                        checked={localFilters.walletRange.includes("201-300")}
                        onChange={() => handleWalletRangeChange("201-300")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-201-300"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 201-300
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-301-400"
                        checked={localFilters.walletRange.includes("301-400")}
                        onChange={() => handleWalletRangeChange("301-400")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-301-400"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 301-400
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-401-500"
                        checked={localFilters.walletRange.includes("401-500")}
                        onChange={() => handleWalletRangeChange("401-500")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-401-500"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 401-500
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wallet-501-plus"
                        checked={localFilters.walletRange.includes("501+")}
                        onChange={() => handleWalletRangeChange("501+")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="wallet-501-plus"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 501+
                      </label>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="walletCustom"
                        checked={customWallet}
                        onChange={() => {
                          const newCustom = !customWallet;
                          setCustomWallet(newCustom);
                          if (newCustom) {
                            setLocalFilters((prev) => ({
                              ...prev,
                              walletRange: [],
                            }));
                          } else {
                            setLocalFilters((prev) => ({
                              ...prev,
                              customWalletMin: "",
                              customWalletMax: "",
                            }));
                            setWalletCustomMaxError("");
                          }
                        }}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="walletCustom"
                        className="text-[14px] nunito-regular text-[#000000]"
                      >
                        Custom
                      </label>
                    </div>
                    {customWallet && (
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                            Min
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Amount"
                            value={localFilters.customWalletMin}
                            onChange={(e) =>
                              handleCustomWalletChange(
                                "customWalletMin",
                                e.target.value.replace(/[^0-9]/g, "")
                              )
                            }
                            className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                            Max
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Amount"
                            value={localFilters.customWalletMax}
                            onChange={(e) =>
                              handleCustomWalletChange(
                                "customWalletMax",
                                e.target.value.replace(/[^0-9]/g, "")
                              )
                            }
                            className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                              walletCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                            }`}
                          />
                          {walletCustomMaxError && (
                            <p className="text-red-500 text-[12px] mt-1">{walletCustomMaxError}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}<div className="border-b mb-3 border-[#DFDFDF]"></div>
            </div>
          )}
          {/* Amount Section for Customer Directory */}
          {user?.role === "Customer" && activeTab !== "Activity" && (
            <>
              <div className="mb-4">
                <div
                  className="flex justify-between items-center mb-4 cursor-pointer"
                  onClick={() =>
                    setSections({ ...sections, amount: !sections.amount })
                  }
                >
                  <span className="nunito-semibold text-[#404040] text-[16px]">
                    Total Amount
                  </span>
                  {sections.amount ? (
                    <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                  ) : (
                    <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                  )}
                </div>
                {sections.amount && (
                  <div className="mb-3">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-0-100"
                          checked={localFilters.amountRange.includes("0-100")}
                          onChange={() => handleAmountRangeChange("0-100")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-0-100"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 0-100
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-101-200"
                          checked={localFilters.amountRange.includes("101-200")}
                          onChange={() => handleAmountRangeChange("101-200")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-101-200"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 101-200
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-201-300"
                          checked={localFilters.amountRange.includes("201-300")}
                          onChange={() => handleAmountRangeChange("201-300")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-201-300"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 201-300
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-301-400"
                          checked={localFilters.amountRange.includes("301-400")}
                          onChange={() => handleAmountRangeChange("301-400")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-301-400"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 301-400
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-401-500"
                          checked={localFilters.amountRange.includes("401-500")}
                          onChange={() => handleAmountRangeChange("401-500")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-401-500"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 401-500
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-501-plus"
                          checked={localFilters.amountRange.includes("501+")}
                          onChange={() => handleAmountRangeChange("501+")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-501-plus"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 501+
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="amountCustom"
                          checked={customAmount}
                          onChange={() => {
                            const newCustom = !customAmount;
                            setCustomAmount(newCustom);
                            if (newCustom) {
                              setLocalFilters((prev) => ({
                                ...prev,
                                amountRange: [],
                              }));
                            } else {
                              setLocalFilters((prev) => ({
                                ...prev,
                                customAmountMin: "",
                                customAmountMax: "",
                              }));
                              setAmountCustomMaxError("");
                            }
                          }}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amountCustom"
                          className="text-[14px] nunito-regular text-[#000000]"
                        >
                          Custom
                        </label>
                      </div>
                      {customAmount && (
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                              Min
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Amount"
                              value={localFilters.customAmountMin}
                              onChange={(e) =>
                                handleCustomAmountChange(
                                  "customAmountMin",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                              Max
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Amount"
                              value={localFilters.customAmountMax}
                              onChange={(e) =>
                                handleCustomAmountChange(
                                  "customAmountMax",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                                amountCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                              }`}
                            />
                            {amountCustomMaxError && (
                              <p className="text-red-500 text-[12px] mt-1">{amountCustomMaxError}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="border-b mb-3 border-[#DFDFDF]"></div>
            </>
          )}
          {/* Activity Amount Section */}
          {activeTab === "Activity" && (
            <div className="mb-4 mt-3">
              <div
                className="flex justify-between items-center mb-4 cursor-pointer"
                onClick={() =>
                  setSections({ ...sections, wallet: !sections.wallet })
                }
              >
                <span className="nunito-semibold text-[#404040] text-[16px]">
                  {user?.role === "Customer" ? "Amount" : "Earnings"}
                </span>
                {sections.wallet ? (
                  <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                ) : (
                  <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                )}
              </div>
              {sections.wallet && (
                <div className="mb-3">
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-0-100"
                        checked={localFilters.amountRange.includes("0-100")}
                        onChange={() => handleAmountRangeChange("0-100")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-0-100"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 0-100
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-101-200"
                        checked={localFilters.amountRange.includes("101-200")}
                        onChange={() => handleAmountRangeChange("101-200")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-101-200"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 101-200
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-201-300"
                        checked={localFilters.amountRange.includes("201-300")}
                        onChange={() => handleAmountRangeChange("201-300")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-201-300"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 201-300
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-301-400"
                        checked={localFilters.amountRange.includes("301-400")}
                        onChange={() => handleAmountRangeChange("301-400")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-301-400"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 301-400
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-401-500"
                        checked={localFilters.amountRange.includes("401-500")}
                        onChange={() => handleAmountRangeChange("401-500")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-401-500"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 401-500
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="activity-amount-501-plus"
                        checked={localFilters.amountRange.includes("501+")}
                        onChange={() => handleAmountRangeChange("501+")}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activity-amount-501-plus"
                        className="text-[14px] nunito-regular text-[#4F4F4F]"
                      >
                        $ 501+
                      </label>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="activityAmountCustom"
                        checked={customAmount}
                        onChange={() => {
                          const newCustom = !customAmount;
                          setCustomAmount(newCustom);
                          if (newCustom) {
                            setLocalFilters((prev) => ({
                              ...prev,
                              amountRange: [],
                            }));
                          } else {
                            setLocalFilters((prev) => ({
                              ...prev,
                              customAmountMin: "",
                              customAmountMax: "",
                            }));
                            setAmountCustomMaxError("");
                          }
                        }}
                        className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                      />
                      <label
                        htmlFor="activityAmountCustom"
                        className="text-[14px] nunito-regular text-[#000000]"
                      >
                        Custom
                      </label>
                    </div>
                    {customAmount && (
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                            Min
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Amount"
                            value={localFilters.customAmountMin}
                            onChange={(e) =>
                              handleCustomAmountChange(
                                "customAmountMin",
                                e.target.value.replace(/[^0-9]/g, "")
                              )
                            }
                            className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040]"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                            Max
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Amount"
                            value={localFilters.customAmountMax}
                            onChange={(e) =>
                              handleCustomAmountChange(
                                "customAmountMax",
                                e.target.value.replace(/[^0-9]/g, "")
                              )
                            }
                            className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] ${
                              amountCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                            }`}
                          />
                          {amountCustomMaxError && (
                            <p className="text-red-500 text-[12px] mt-1">{amountCustomMaxError}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
          <div className="border-b mb-3 border-[#DFDFDF]"></div>
            </div>
          )}
          {/* Amount/Earnings Section for Expert Directory */}
          {activeTab !== "Activity" && user?.role === "Expert" && (
            <>
              <div className="mb-4">
                <div
                  className="flex justify-between items-center mb-4 cursor-pointer"
                  onClick={() =>
                    setSections({ ...sections, amount: !sections.amount })
                  }
                >
                  <span className="nunito-semibold text-[#404040] text-[16px]">
                    Total Earnings
                  </span>
                  {sections.amount ? (
                    <ChevronUp className="w-[24px] h-[24px] text-[#404040]" />
                  ) : (
                    <ChevronDown className="w-[24px] h-[24px] text-[#404040]" />
                  )}
                </div>
                {sections.amount && (
                  <div className="mb-3">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-0-100"
                          checked={localFilters.amountRange.includes("0-100")}
                          onChange={() => handleAmountRangeChange("0-100")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-0-100"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 0-100
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-101-200"
                          checked={localFilters.amountRange.includes("101-200")}
                          onChange={() => handleAmountRangeChange("101-200")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-101-200"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 101-200
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-201-300"
                          checked={localFilters.amountRange.includes("201-300")}
                          onChange={() => handleAmountRangeChange("201-300")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-201-300"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 201-300
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-301-400"
                          checked={localFilters.amountRange.includes("301-400")}
                          onChange={() => handleAmountRangeChange("301-400")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-301-400"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 301-400
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-401-500"
                          checked={localFilters.amountRange.includes("401-500")}
                          onChange={() => handleAmountRangeChange("401-500")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-401-500"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 401-500
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="amount-501-plus"
                          checked={localFilters.amountRange.includes("501+")}
                          onChange={() => handleAmountRangeChange("501+")}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amount-501-plus"
                          className="text-[14px] nunito-regular text-[#4F4F4F]"
                        >
                          $ 501+
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id="amountCustom"
                          checked={customAmount}
                          onChange={() => {
                            const newCustom = !customAmount;
                            setCustomAmount(newCustom);
                            if (newCustom) {
                              setLocalFilters((prev) => ({
                                ...prev,
                                amountRange: [],
                              }));
                            } else {
                              setLocalFilters((prev) => ({
                                ...prev,
                                customAmountMin: "",
                                customAmountMax: "",
                              }));
                              setAmountCustomMaxError("");
                            }
                          }}
                          className="h-[16px] w-[16px] mr-2  accent-[#F15D18] "
                        />
                        <label
                          htmlFor="amountCustom"
                          className="text-[14px] nunito-regular text-[#000000]"
                        >
                          Custom
                        </label>
                      </div>
                      {customAmount && (
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                              Min
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Amount"
                              value={localFilters.customAmountMin}
                              onChange={(e) =>
                                handleCustomAmountChange(
                                  "customAmountMin",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              className="w-full h-[49px] p-2 border border-[#D9D9D9] rounded-[8px] text-[14px] text-[#404040] nunito-normal"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[14px] nunito-semibold text-[#404040] mb-1">
                              Max
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Amount"
                              value={localFilters.customAmountMax}
                              onChange={(e) =>
                                handleCustomAmountChange(
                                  "customAmountMax",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              className={`w-full h-[49px] p-2 border rounded-[8px] text-[14px] text-[#404040] nunito-normal ${
                                amountCustomMaxError ? 'border-red-500' : 'border-[#D9D9D9]'
                              }`}
                            />
                            {amountCustomMaxError && (
                              <p className="text-red-500 text-[12px] mt-1">{amountCustomMaxError}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="border-b mb-3 border-[#DFDFDF]"></div>{" "}
            </>
          )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <button
              className="p-3 w-[143px] h-[48px] border border-[#F15D18] text-[#F15D18] text-[16px] nunito-medium rounded-[8px]"
              onClick={handleCancel}
            >
              Cancel
            </button>{" "}
            <button
              className={`p-3 w-[201px] h-[48px] text-[16px] nunito-semibold rounded-[8px] ${
                hasValidationErrors()
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-[#F15D18] text-white'
              }`}
              onClick={handleApply}
              disabled={hasValidationErrors()}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModelTeam;
